import { Skeleton, SkeletonBadge, SkeletonButton, SkeletonText } from '@/components/ui/skeleton';

interface DetailHeaderSkeletonProps {
    /** Width of the record-name placeholder. */
    readonly titleWidth?: string;
    /** Caption under the title. Pass `null` for headers that have none. */
    readonly descriptionWidth?: string | null;
    /** Status pill beside the title, as on poll detail and workflow analytics. */
    readonly showStatusBadge?: boolean;
    /** Pixel widths of the trailing action buttons, left to right. */
    readonly actionWidths?: readonly number[];
    /** `text-xl` on workflow/sequence detail screens, `text-2xl` on poll detail. */
    readonly titleSize?: 'xl' | 'h1';
}

/**
 * Loading placeholder for a detail-screen header: back arrow, record name with
 * an optional status pill, caption, and right-aligned actions.
 *
 * Use this only for headers whose data is genuinely still loading. Where the
 * title comes from a query that has already resolved — which is the common case,
 * since detail screens fetch the record and its report separately — render the
 * real header and skeleton only the body below it.
 */
export function DetailHeaderSkeleton({
    titleWidth = '220px',
    descriptionWidth = '280px',
    showStatusBadge = true,
    actionWidths = [],
    titleSize = 'xl',
}: DetailHeaderSkeletonProps) {
    return (
        <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
                {/* `size-icon-sm` back button — 32px square. */}
                <Skeleton className="size-8 shrink-0 rounded-md" />
                <div>
                    <div className="flex items-center gap-2">
                        <SkeletonText size={titleSize} width={titleWidth} />
                        {showStatusBadge && <SkeletonBadge width={72} />}
                    </div>
                    {descriptionWidth !== null && (
                        <SkeletonText size="xs" width={descriptionWidth} className="mt-0.5" />
                    )}
                </div>
            </div>
            {actionWidths.length > 0 && (
                <div className="flex items-center gap-2">
                    {actionWidths.map((width, i) => (
                        <SkeletonButton key={i} size="sm" width={width} />
                    ))}
                </div>
            )}
        </div>
    );
}
